import React, { useEffect, useMemo, useState } from "react";
import { Modal, message } from "antd";
import Cookies from "js-cookie";

/**
 * ============================================================
 * Copy / Options
 * ============================================================
 */
const signupReasons = [
  {
    key: "personal",
    label: "Apply for Personal Financing",
    desc: "For your personal plans — consolidate, renovate, learn, or handle surprises.",
    icon: "💳",
  },
  {
    key: "corporate",
    label: "Apply for Business / Corporate Financing",
    desc: "Fuel growth — working capital, trade, equipment, expansion.",
    icon: "🏢",
  },
  {
    key: "cbs",
    label: "Check my credit standing (CBS report)",
    desc: "Understand your credit standing before you apply.",
    icon: "📈",
  },
  {
    key: "browse",
    label: "Explore financing options (just browsing)",
    desc: "See what’s available and compare options.",
    icon: "🧭",
  },
  {
    key: "others",
    label: "Something else",
    desc: "Tell us what you’re here for.",
    icon: "✨",
  },
];

const personalGoalOptions = [
  { label: "Debt consolidation", icon: "🧩" },
  { label: "Home renovation", icon: "🏠" },
  { label: "Education / upskilling", icon: "🎓" },
  { label: "Medical expenses", icon: "🩺" },
  { label: "Wedding / family milestones", icon: "💍" },
  { label: "Travel / relocation", icon: "✈️" },
  { label: "Emergency cashflow", icon: "🚨" },
  { label: "Personal investment / side project", icon: "🚀" },
  { label: "Other (tell us briefly)", icon: "✍️" },
];

const businessGoalOptions = [
  { label: "Business expansion / growth", icon: "📊" },
  { label: "Working capital / cashflow support", icon: "💼" },
  { label: "Trade financing (PO / invoice / import-export)", icon: "🚢" },
  { label: "Equipment / machinery purchase", icon: "🛠️" },
  { label: "Inventory financing", icon: "📦" },
  { label: "Bridging loan", icon: "🌉" },
  { label: "Refinancing / restructuring", icon: "🔁" },
  { label: "Startup funding", icon: "🌱" },
  { label: "Other (tell us briefly)", icon: "✍️" },
];

const cbsReasonOptions = [
  { label: "For employment, background check", icon: "🧑‍💼" },
  { label: "Planning to apply for personal loan or credit card", icon: "💳" },
  { label: "Planning to apply for business financing", icon: "🏢" },
  { label: "Property or mortgage application", icon: "🏠" },
  { label: "Was told there may be an issue with my CBS", icon: "⚠️" },
  { label: "Required by Bank / Financial Institution", icon: "🏦" },
];

const BRAND = "var(--br-primary, rgb(14, 90, 165))";
const BRAND_2 = "rgb(109, 92, 246)"; // accent
const BG = "#F3F7FF";

const toggleSelection = (value, setter, current) => {
  if (current.includes(value)) setter(current.filter((item) => item !== value));
  else setter([...current, value]);
};

/**
 * ============================================================
 * Small UI helpers
 * ============================================================
 */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const ProgressBar = ({ stepIndex = 0, steps = 4 }) => {
  const pct = clamp(((stepIndex + 1) / steps) * 100, 0, 100);
  return (
    <div style={{ width: "100%", marginTop: 10 }}>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: "rgba(148,163,184,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_2} 100%)`,
          }}
        />
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: "rgba(15,23,42,0.65)" }}>
        Step {stepIndex + 1} of {steps}
      </div>
    </div>
  );
};

const PrimaryButton = ({ disabled, children, onClick, style }) => (
  <button
    onClick={disabled ? undefined : onClick}
    style={{
      border: "none",
      outline: "none",
      borderRadius: "var(--br-card-radius, 12px)",
      padding: "12px 18px",
      fontWeight: 800,
      cursor: disabled ? "not-allowed" : "pointer",
      color: "white",
      background: disabled
        ? "rgba(148,163,184,0.55)"
        : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_2} 120%)`,
      boxShadow: disabled ? "none" : "var(--br-shadow, 0 10px 22px rgba(var(--br-primary-rgb, 14,90,165), 0.22))",
      transition:
        "transform .12s ease, box-shadow .12s ease, opacity .12s ease",
      opacity: disabled ? 0.8 : 1,
      ...style,
    }}
    onMouseDown={(e) => {
      if (!disabled) e.currentTarget.style.transform = "translateY(1px)";
    }}
    onMouseUp={(e) => {
      if (!disabled) e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, style }) => (
  <button
    onClick={onClick}
    style={{
      borderRadius: "var(--br-card-radius, 12px)",
      padding: "12px 16px",
      fontWeight: 800,
      cursor: "pointer",
      border: "1px solid rgba(148,163,184,0.45)",
      background: "var(--br-surface, rgba(255,255,255,0.75))",
      color: "rgba(15,23,42,0.8)",
      transition: "transform .12s ease, background .12s ease",
      ...style,
    }}
  >
    {children}
  </button>
);

const Input = (props) => (
  <input
    {...props}
    style={{
      width: "100%",
      borderRadius: "var(--br-card-radius, 12px)",
      border: "1px solid rgba(148,163,184,0.35)",
      padding: "12px 14px",
      outline: "none",
      fontSize: 13,
      background: "var(--br-surface, rgba(255,255,255,0.85))",
      boxShadow: "var(--br-shadow, 0 8px 20px rgba(2,8,23,0.04))",
      ...props.style,
    }}
    onFocus={(e) => {
      e.currentTarget.style.border = `1px solid rgba(14, 90, 165, 0.55)`;
      e.currentTarget.style.boxShadow = "0 10px 22px rgba(14, 90, 165, 0.12)";
      props.onFocus?.(e);
    }}
    onBlur={(e) => {
      e.currentTarget.style.border = "1px solid rgba(148,163,184,0.35)";
      e.currentTarget.style.boxShadow = "0 8px 20px rgba(2,8,23,0.04)";
      props.onBlur?.(e);
    }}
  />
);

const Tile = ({ active, title, desc, icon, onClick }) => (
  <div
    onClick={onClick}
    style={{
      borderRadius: "var(--br-card-radius, 16px)",
      padding: 14,
      cursor: "pointer",
      border: active
        ? `1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.55)`
        : "1px solid rgba(148,163,184,0.32)",
      background: active
        ? "linear-gradient(180deg, rgba(var(--br-primary-rgb, 14,90,165), 0.10) 0%, rgba(109,92,246,0.06) 100%)"
        : "var(--br-surface, rgba(255,255,255,0.8))",
      boxShadow: active
        ? "var(--br-shadow, 0 14px 34px rgba(var(--br-primary-rgb, 14,90,165), 0.14))"
        : "var(--br-shadow, 0 10px 26px rgba(2, 8, 23, 0.06))",
      transition: "transform .12s ease, box-shadow .12s ease, border .12s ease",
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: "var(--br-card-radius, 14px)",
        background: active
          ? `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_2} 120%)`
          : "rgba(148,163,184,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        color: active ? "white" : "rgba(15,23,42,0.8)",
        flex: "0 0 auto",
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div
        style={{ fontWeight: 900, color: "rgba(15,23,42,0.88)", fontSize: 13 }}
      >
        {title}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          color: "rgba(15,23,42,0.62)",
          lineHeight: 1.35,
        }}
      >
        {desc}
      </div>
    </div>
    {active ? (
      <div
        style={{ fontSize: 16, color: BRAND, marginTop: 2, fontWeight: 900 }}
      >
        ✓
      </div>
    ) : null}
  </div>
);

const TagPill = ({ active, label, icon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: "none",
      cursor: "pointer",
      borderRadius: 999,
      padding: "10px 12px",
      fontSize: 13,
      fontWeight: 800,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: active
        ? "linear-gradient(135deg, rgba(var(--br-primary-rgb, 14,90,165), 0.14) 0%, rgba(109,92,246,0.10) 120%)"
        : "var(--br-surface, rgba(255,255,255,0.8))",
      color: "rgba(15,23,42,0.8)",
      border: active
        ? `1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.45)`
        : "1px solid rgba(148,163,184,0.32)",
      boxShadow: active
        ? "var(--br-shadow, 0 12px 26px rgba(var(--br-primary-rgb, 14,90,165), 0.12))"
        : "var(--br-shadow, 0 10px 20px rgba(2,8,23,0.05))",
      transition: "transform .12s ease",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
  >
    <span style={{ fontSize: 16 }}>{icon}</span>
    {label}
    {active ? <span style={{ color: BRAND, fontWeight: 900 }}>✓</span> : null}
  </button>
);

/**
 * ============================================================
 * Inline “fun fintech” illustrations (no assets needed)
 * ============================================================
 */
const IllustrationPhone = () => (
  <svg width="100%" viewBox="0 0 520 220" style={{ maxHeight: 160 }}>
    <defs>
      <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={BRAND} stopOpacity="1" />
        <stop offset="1" stopColor={BRAND_2} stopOpacity="1" />
      </linearGradient>
      <filter id="sh" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow
          dx="0"
          dy="12"
          stdDeviation="12"
          floodColor="#0b1b3a"
          floodOpacity="0.14"
        />
      </filter>
    </defs>
    <rect
      x="0"
      y="0"
      width="520"
      height="220"
      rx="22"
      fill="rgba(255,255,255,0.65)"
    />
    <circle cx="90" cy="55" r="55" fill="rgba(var(--br-primary-rgb, 14,90,165), 0.12)" />
    <circle cx="480" cy="35" r="80" fill="rgba(109,92,246,0.10)" />
    <g filter="url(#sh)">
      <rect x="170" y="30" width="180" height="160" rx="26" fill="white" />
      <rect
        x="190"
        y="56"
        width="140"
        height="104"
        rx="18"
        fill="url(#g1)"
        opacity="0.12"
      />
      <rect
        x="208"
        y="68"
        width="104"
        height="18"
        rx="9"
        fill="url(#g1)"
        opacity="0.65"
      />
      <rect
        x="208"
        y="98"
        width="126"
        height="12"
        rx="6"
        fill="rgba(15,23,42,0.18)"
      />
      <rect
        x="208"
        y="118"
        width="92"
        height="12"
        rx="6"
        fill="rgba(15,23,42,0.14)"
      />
      <circle cx="260" cy="165" r="10" fill="rgba(15,23,42,0.12)" />
    </g>
    <g>
      <circle cx="110" cy="150" r="26" fill="url(#g1)" opacity="0.18" />
      <path
        d="M105 150 l7 7 l16-18"
        fill="none"
        stroke={BRAND}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="420" cy="150" r="20" fill="rgba(109,92,246,0.16)" />
      <path
        d="M420 140 v20"
        stroke={BRAND_2}
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path
        d="M410 150 h20"
        stroke={BRAND_2}
        strokeWidth="6"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

const IllustrationIntent = () => (
  <svg width="100%" viewBox="0 0 520 220" style={{ maxHeight: 160 }}>
    <defs>
      <linearGradient id="g2" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={BRAND} />
        <stop offset="1" stopColor={BRAND_2} />
      </linearGradient>
    </defs>
    <rect
      x="0"
      y="0"
      width="520"
      height="220"
      rx="22"
      fill="rgba(255,255,255,0.65)"
    />
    <circle cx="70" cy="170" r="70" fill="rgba(var(--br-primary-rgb, 14,90,165), 0.10)" />
    <circle cx="470" cy="55" r="80" fill="rgba(109,92,246,0.10)" />
    <rect
      x="85"
      y="55"
      width="150"
      height="105"
      rx="18"
      fill="rgba(var(--br-primary-rgb, 14,90,165), 0.12)"
    />
    <rect
      x="255"
      y="55"
      width="180"
      height="45"
      rx="18"
      fill="rgba(109,92,246,0.12)"
    />
    <rect
      x="255"
      y="115"
      width="180"
      height="45"
      rx="18"
      fill="url(#g2)"
      opacity="0.12"
    />
    <circle cx="120" cy="90" r="14" fill="url(#g2)" opacity="0.55" />
    <rect
      x="145"
      y="82"
      width="70"
      height="16"
      rx="8"
      fill="rgba(15,23,42,0.18)"
    />
    <rect
      x="145"
      y="106"
      width="55"
      height="12"
      rx="6"
      fill="rgba(15,23,42,0.12)"
    />
    <circle cx="290" cy="78" r="10" fill="rgba(var(--br-primary-rgb, 14,90,165), 0.22)" />
    <rect
      x="308"
      y="70"
      width="110"
      height="16"
      rx="8"
      fill="rgba(15,23,42,0.18)"
    />
    <circle cx="290" cy="138" r="10" fill="rgba(109,92,246,0.22)" />
    <rect
      x="308"
      y="130"
      width="120"
      height="16"
      rx="8"
      fill="rgba(15,23,42,0.18)"
    />
  </svg>
);

const IllustrationWelcome = () => (
  <svg width="100%" viewBox="0 0 520 220" style={{ maxHeight: 160 }}>
    <defs>
      <linearGradient id="g3" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={BRAND} />
        <stop offset="1" stopColor={BRAND_2} />
      </linearGradient>
    </defs>
    <rect
      x="0"
      y="0"
      width="520"
      height="220"
      rx="22"
      fill="rgba(255,255,255,0.65)"
    />
    <circle cx="90" cy="60" r="70" fill="rgba(var(--br-primary-rgb, 14,90,165), 0.10)" />
    <circle cx="470" cy="160" r="80" fill="rgba(109,92,246,0.10)" />
    <g>
      <rect x="140" y="58" width="240" height="120" rx="20" fill="white" />
      <rect
        x="160"
        y="80"
        width="200"
        height="16"
        rx="8"
        fill="rgba(15,23,42,0.16)"
      />
      <rect
        x="160"
        y="106"
        width="160"
        height="12"
        rx="6"
        fill="rgba(15,23,42,0.12)"
      />
      <rect
        x="160"
        y="126"
        width="110"
        height="12"
        rx="6"
        fill="rgba(15,23,42,0.10)"
      />
      <rect
        x="160"
        y="150"
        width="120"
        height="16"
        rx="8"
        fill="url(#g3)"
        opacity="0.22"
      />
      <circle cx="360" cy="150" r="18" fill="url(#g3)" opacity="0.6" />
      <path
        d="M352 150 l6 6 l14-16"
        fill="none"
        stroke="white"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

/**
 * ============================================================
 * Main Wizard
 * ============================================================
 */
const WelcomeWizard = ({
  isOpen,
  userDetails,
  accountManagerExist,
  onFinish,
  onOpenAccountManager,
}) => {
  const [wizardStep, setWizardStep] = useState("verify");

  const [signupReason, setSignupReason] = useState("");
  const [personalGoals, setPersonalGoals] = useState([]);
  const [businessGoals, setBusinessGoals] = useState([]);
  const [personalOtherText, setPersonalOtherText] = useState("");
  const [businessOtherText, setBusinessOtherText] = useState("");
  const [cbsReason, setCbsReason] = useState("");

  const [hasMobileVerified, setHasMobileVerified] = useState(
    Boolean(userDetails?.mobile_no)
  );
  const [mobileField, setMobileField] = useState("");
  const [otpField, setOtpField] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpSessiondata, setOtpSessionData] = useState(null);

  const [screenWidth, setScreenWidth] = useState(1920);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(
    Cookies.get("ismobile") === "yes" ? true : false
  );
  useEffect(() => {
    if (screenWidth < 1200) {
      Cookies.set("ismobile", "yes");
      setIsMobile(true);
    } else {
      Cookies.set("ismobile", "no");
      setIsMobile(false);
    }
  }, [screenWidth]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Call handleResize initially to set the initial screen width
    handleResize();

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    if (!isOpen) return;

    const verified = Boolean(userDetails?.mobile_no);
    setHasMobileVerified(verified);

    // reset otp UI each open
    setOtpSent(false);
    setOtpField("");
    setOtpSessionData(null);

    if (!verified) setWizardStep("verify");
    else setWizardStep("reason");
  }, [isOpen, userDetails]);

  const normalizeMobile = (input) => {
    let clean = String(input || "").replace(/\D/g, "");
    if (!clean) return "";
    if (clean.startsWith("65")) return clean;
    return "65" + clean;
  };

  const sendUserOTP = async () => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/auth/register/mobile/sendOTP`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify({ mobile_no: normalizeMobile(mobileField) }),
        }
      );

      if (response.ok) {
        const json = await response.json();
        message.success("WhatsApp OTP sent!");
        setOtpSessionData(json.data.session);
        setOtpSent(true);
      } else {
        message.error(
          "Failed to send OTP. Please check your number and try again."
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Network error while sending OTP.");
    }
  };

  const verifyUserOTP = async () => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/auth/register/mobile/verifyOtp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify({ otp: otpField, session: otpSessiondata }),
        }
      );

      if (response.ok) {
        setHasMobileVerified(true);
        setWizardStep("reason");
        message.success("Mobile number verification successful!");
      } else {
        message.error(
          "The OTP is incorrect. Try again or tap Resend OTP to get a new code."
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Network error while verifying OTP.");
    }
  };

  const handleWizardFinish = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/user/welcome-wizard",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify({
            signup_reason: signupReason,
            personal_goals: personalGoals,
            business_goals: businessGoals,
            personal_other_text: personalOtherText || null,
            business_other_text: businessOtherText || null,
            cbs_reason: cbsReason || null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save welcome wizard response");
      }
    } catch (error) {
      console.error(error);
      message.error(
        "We couldn't save your responses right now. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
      onFinish?.();
    }
  };

  const stepsMeta = useMemo(() => {
    // only count verify step if mobile not verified
    const base = [];
    if (!hasMobileVerified) base.push("verify");
    base.push("reason");

    // branch step: personal/business only if needed
    if (signupReason === "Apply for Personal Financing") base.push("personal");
    else if (signupReason === "Apply for Business / Corporate Financing")
      base.push("business");
    else if (signupReason === "Check my credit standing (CBS report)")
      base.push("cbs");

    base.push("done");
    return base;
  }, [hasMobileVerified, signupReason]);

  const stepIndex = useMemo(() => {
    const idx = stepsMeta.indexOf(wizardStep);
    return idx === -1 ? 0 : idx;
  }, [stepsMeta, wizardStep]);

  const heroIllustration = useMemo(() => {
    if (wizardStep === "verify") return <IllustrationPhone />;
    if (wizardStep === "reason") return <IllustrationIntent />;
    return <IllustrationWelcome />;
  }, [wizardStep]);

  const canGoNextFromReason = Boolean(signupReason);

  const goNextFromReason = () => {
    if (!signupReason) return;
    if (signupReason === "Apply for Personal Financing")
      setWizardStep("personal");
    else if (signupReason === "Apply for Business / Corporate Financing")
      setWizardStep("business");
    else if (signupReason === "Check my credit standing (CBS report)")
      setWizardStep("cbs");
    else setWizardStep("done");
  };

  return (
    <Modal
      open={isOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered={false}
      width="100%"
      style={{ top: 0, padding: 0, maxWidth: "100vw" }}
      bodyStyle={{ padding: 0, height: "100vh", background: BG }}
      footer={null}
      className="remove_padding"
    >
      {/* Full-screen fun fintech background */}
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          background: `radial-gradient(900px 400px at 10% 10%, rgba(var(--br-primary-rgb, 14,90,165), 0.20), transparent 60%),
                    radial-gradient(900px 500px at 90% 20%, rgba(109,92,246,0.18), transparent 55%),
                    radial-gradient(700px 500px at 80% 90%, rgba(var(--br-primary-rgb, 14,90,165), 0.12), transparent 55%),
                    linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(243,247,255,1) 55%, rgba(241,246,249,1) 100%)`,
        }}
      >
        {/* floating blobs */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0.85,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -120,
              left: -120,
              width: 320,
              height: 320,
              borderRadius: 999,
              background: "rgba(var(--br-primary-rgb, 14,90,165), 0.14)",
              filter: "blur(2px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 40,
              right: -140,
              width: 340,
              height: 340,
              borderRadius: 999,
              background: "rgba(109,92,246,0.14)",
              filter: "blur(2px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -160,
              left: "35%",
              width: 420,
              height: 420,
              borderRadius: 999,
              background: "rgba(var(--br-primary-rgb, 14,90,165), 0.10)",
              filter: "blur(2px)",
            }}
          />
        </div>

        {/* Center layout */}
        <div
          className="wizard-shell"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "28px 18px",
          }}
        >
          <div
            className="wizard-grid"
            style={{
              width: "100%",
              maxWidth: 980,
              display: "grid",
              gridTemplateColumns: "420px 1fr",
              gap: 18,
            }}
          >
            {/* Left “hero” card */}
            {!isMobile ? (
              <div
                className="wizard-hero"
                style={{
                  borderRadius: "var(--br-card-radius, 22px)",
                  background: "var(--br-surface, rgba(255,255,255,0.75))",
                  border: "1px solid rgba(148,163,184,0.30)",
                  boxShadow: "var(--br-shadow, 0 18px 46px rgba(2, 8, 23, 0.08))",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "18px 18px 14px 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid rgba(148,163,184,0.22)",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "var(--br-card-radius, 12px)",
                        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_2} 120%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: 900,
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 900,
                          color: "rgba(15,23,42,0.88)",
                        }}
                      >
                        SmartLend
                      </div>
                      <div
                        style={{ fontSize: 12, color: "rgba(15,23,42,0.55)" }}
                      >
                        Quick setup (borrower)
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: "rgba(var(--br-primary-rgb, 14,90,165), 0.10)",
                      color: BRAND,
                      fontWeight: 900,
                      border: "1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.18)",
                    }}
                  >
                    Secure onboarding
                  </div>
                </div>

                <div style={{ padding: 16 }}>
                  {heroIllustration}
                  <ProgressBar stepIndex={stepIndex} steps={stepsMeta.length} />
                  <div
                    style={{
                      marginTop: 14,
                      fontSize: 12,
                      color: "rgba(15,23,42,0.65)",
                      lineHeight: 1.45,
                    }}
                  >
                    We’ll personalize your experience in under a minute. Your
                    info helps us route you to the right financing flow.
                  </div>
                </div>
              </div>
            ) : null}

            {/* Right main wizard card */}
            <div
              className="wizard-main"
              style={{
                borderRadius: "var(--br-card-radius, 22px)",
                background: "var(--br-surface, rgba(255,255,255,0.82))",
                border: "1px solid rgba(148,163,184,0.30)",
                boxShadow: "var(--br-shadow, 0 18px 46px rgba(2, 8, 23, 0.08))",
                overflow: "hidden",
                minHeight: 520,
              }}
            >
              <div style={{ padding: "22px 22px 0 22px" }}>
                {/* VERIFY */}
                {wizardStep === "verify" && !hasMobileVerified ? (
                  <>
                    <div
                      className="wizard-title"
                      style={{
                        fontWeight: 1000,
                        fontSize: 22,
                        color: "rgba(15,23,42,0.9)",
                      }}
                    >
                      Verify your mobile number 📱
                    </div>
                    <div
                      className="wizard-subtitle"
                      style={{
                        marginTop: 8,
                        fontSize: 13,
                        color: "rgba(15,23,42,0.62)",
                      }}
                    >
                      We’ll send a WhatsApp OTP to confirm it’s really you.
                    </div>

                    <div style={{ marginTop: 18 }}>
                      {!otpSent ? (
                        <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "140px 1fr",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                borderRadius: "var(--br-card-radius, 12px)",
                                border: "1px solid rgba(148,163,184,0.35)",
                                background: "var(--br-surface, rgba(255,255,255,0.85))",
                                padding: "12px 12px",
                                fontWeight: 900,
                                color: "rgba(15,23,42,0.75)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>🇸🇬 +65</span>
                              <span style={{ fontSize: 12, opacity: 0.7 }}>
                                SG
                              </span>
                            </div>
                            <Input
                              value={mobileField}
                              onChange={(e) => setMobileField(e.target.value)}
                              placeholder="Enter your mobile number"
                            />
                          </div>

                          <div
                            className="wizard-actions"
                            style={{
                              marginTop: 16,
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <PrimaryButton
                              disabled={!normalizeMobile(mobileField)}
                              onClick={sendUserOTP}
                            >
                              Send OTP →
                            </PrimaryButton>
                          </div>

                          <div
                            style={{
                              marginTop: 10,
                              fontSize: 12,
                              color: "rgba(15,23,42,0.55)",
                            }}
                          >
                            By continuing, you agree to receive verification
                            messages for account security.
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              marginTop: 6,
                              padding: "12px 14px",
                              borderRadius: "var(--br-card-radius, 14px)",
                              background: "rgba(var(--br-primary-rgb, 14,90,165), 0.08)",
                              border: "1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.16)",
                              color: "rgba(15,23,42,0.75)",
                              fontSize: 13,
                            }}
                          >
                            ✅ OTP sent. Please check WhatsApp.
                          </div>

                          <div
                            style={{
                              marginTop: 14,
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 10,
                            }}
                          >
                            <Input
                              value={otpField}
                              onChange={(e) => setOtpField(e.target.value)}
                              placeholder="Enter OTP (e.g., 123456)"
                            />
                            <GhostButton onClick={sendUserOTP}>
                              Resend OTP
                            </GhostButton>
                          </div>

                          <div
                            className="wizard-actions"
                            style={{
                              marginTop: 16,
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <PrimaryButton
                              disabled={!otpField || !otpSessiondata}
                              onClick={verifyUserOTP}
                            >
                              Verify →
                            </PrimaryButton>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : null}

                {/* REASON */}
                {wizardStep === "reason" ? (
                  <>
                    <div
                      className="wizard-title"
                      style={{
                        fontWeight: 1000,
                        fontSize: 22,
                        color: "rgba(15,23,42,0.9)",
                      }}
                    >
                      What brings you to SmartLend? ✨
                    </div>
                    <div
                      className="wizard-subtitle"
                      style={{
                        marginTop: 8,
                        fontSize: 13,
                        color: "rgba(15,23,42,0.62)",
                      }}
                    >
                      Pick the option that best matches your goal today.
                    </div>

                    <div
                      className="wizard-tiles"
                      style={{
                        marginTop: 16,
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 12,
                      }}
                    >
                      {signupReasons.map((r) => (
                        <Tile
                          key={r.key}
                          active={signupReason === r.label}
                          title={r.label}
                          desc={r.desc}
                          icon={r.icon}
                          onClick={() => setSignupReason(r.label)}
                        />
                      ))}
                    </div>

                    <div
                      className="wizard-actions"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        marginTop: 18,
                        paddingBottom: 20,
                      }}
                    >
                      <PrimaryButton
                        disabled={!canGoNextFromReason}
                        onClick={goNextFromReason}
                      >
                        Continue →
                      </PrimaryButton>
                    </div>
                  </>
                ) : null}

                {/* PERSONAL */}
                {wizardStep === "personal" ? (
                  <>
                    <div
                      className="wizard-title"
                      style={{
                        fontWeight: 1000,
                        fontSize: 22,
                        color: "rgba(15,23,42,0.9)",
                      }}
                    >
                      What are you looking to achieve? 🚀
                    </div>
                    <div
                      className="wizard-subtitle"
                      style={{
                        marginTop: 8,
                        fontSize: 13,
                        color: "rgba(15,23,42,0.62)",
                      }}
                    >
                      Select all that apply — we’ll guide you to the right
                      options.
                    </div>

                    <div
                      className="wizard-pills"
                      style={{
                        marginTop: 16,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      {personalGoalOptions.map((opt) => (
                        <TagPill
                          key={opt.label}
                          label={opt.label}
                          icon={opt.icon}
                          active={personalGoals.includes(opt.label)}
                          onClick={() =>
                            toggleSelection(
                              opt.label,
                              setPersonalGoals,
                              personalGoals
                            )
                          }
                        />
                      ))}
                    </div>

                    {personalGoals.includes("Other (tell us briefly)") ? (
                      <div style={{ marginTop: 14 }}>
                        <Input
                          value={personalOtherText}
                          onChange={(e) => setPersonalOtherText(e.target.value)}
                          placeholder="Tell us briefly..."
                        />
                      </div>
                    ) : null}

                    <div
                      className="wizard-actions"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        marginTop: 18,
                        paddingBottom: 20,
                      }}
                    >
                      <GhostButton onClick={() => setWizardStep("reason")}>
                        ← Back
                      </GhostButton>
                      <PrimaryButton
                        disabled={!personalGoals.length}
                        onClick={() => setWizardStep("done")}
                      >
                        Continue →
                      </PrimaryButton>
                    </div>
                  </>
                ) : null}

                {/* BUSINESS */}
                {wizardStep === "business" ? (
                  <>
                    <div
                      className="wizard-title"
                      style={{
                        fontWeight: 1000,
                        fontSize: 22,
                        color: "rgba(15,23,42,0.9)",
                      }}
                    >
                      What type of financing do you need? 🏢
                    </div>
                    <div
                      className="wizard-subtitle"
                      style={{
                        marginTop: 8,
                        fontSize: 13,
                        color: "rgba(15,23,42,0.62)",
                      }}
                    >
                      Select all that apply — we’ll route you to the right flow.
                    </div>

                    <div
                      className="wizard-pills"
                      style={{
                        marginTop: 16,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      {businessGoalOptions.map((opt) => (
                        <TagPill
                          key={opt.label}
                          label={opt.label}
                          icon={opt.icon}
                          active={businessGoals.includes(opt.label)}
                          onClick={() =>
                            toggleSelection(
                              opt.label,
                              setBusinessGoals,
                              businessGoals
                            )
                          }
                        />
                      ))}
                    </div>

                    {businessGoals.includes("Other (tell us briefly)") ? (
                      <div style={{ marginTop: 14 }}>
                        <Input
                          value={businessOtherText}
                          onChange={(e) => setBusinessOtherText(e.target.value)}
                          placeholder="Tell us briefly..."
                        />
                      </div>
                    ) : null}

                    <div
                      className="wizard-actions"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        marginTop: 18,
                        paddingBottom: 20,
                      }}
                    >
                      <GhostButton onClick={() => setWizardStep("reason")}>
                        ← Back
                      </GhostButton>
                      <PrimaryButton
                        disabled={!businessGoals.length}
                        onClick={() => setWizardStep("done")}
                      >
                        Continue →
                      </PrimaryButton>
                    </div>
                  </>
                ) : null}

                {/* CBS REASON */}
                {wizardStep === "cbs" ? (
                  <>
                    <div
                      className="wizard-title"
                      style={{
                        fontWeight: 1000,
                        fontSize: 22,
                        color: "rgba(15,23,42,0.9)",
                      }}
                    >
                      What’s your reason for checking your credit standing? 📋
                    </div>
                    <div
                      className="wizard-subtitle"
                      style={{
                        marginTop: 8,
                        fontSize: 13,
                        color: "rgba(15,23,42,0.62)",
                      }}
                    >
                      Choose the option that best matches your situation.
                    </div>

                    <div
                      className="wizard-pills"
                      style={{
                        marginTop: 16,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      {cbsReasonOptions.map((opt) => (
                        <TagPill
                          key={opt.label}
                          label={opt.label}
                          icon={opt.icon}
                          active={cbsReason === opt.label}
                          onClick={() => setCbsReason(opt.label)}
                        />
                      ))}
                    </div>

                    <div
                      className="wizard-actions"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        marginTop: 18,
                        paddingBottom: 20,
                      }}
                    >
                      <GhostButton onClick={() => setWizardStep("reason")}>
                        ← Back
                      </GhostButton>
                      <PrimaryButton
                        disabled={!cbsReason}
                        onClick={() => setWizardStep("done")}
                      >
                        Continue →
                      </PrimaryButton>
                    </div>
                  </>
                ) : null}

                {/* DONE */}
                {wizardStep === "done" ? (
                  <>
                    <div
                      className="wizard-title"
                      style={{
                        fontWeight: 1000,
                        fontSize: 24,
                        color: "rgba(15,23,42,0.92)",
                      }}
                    >
                      You’re all set — welcome aboard! 🎉
                    </div>
                    <div
                      className="wizard-subtitle"
                      style={{
                        marginTop: 10,
                        fontSize: 13,
                        color: "rgba(15,23,42,0.62)",
                        lineHeight: 1.55,
                      }}
                    >
                      If you need help at any time, you can reach your Account
                      Manager. Tap below to view their details.
                    </div>

                    {accountManagerExist ? (
                      <div
                        style={{
                          marginTop: 16,
                          borderRadius: "var(--br-card-radius, 18px)",
                          padding: 14,
                          background:
                            "linear-gradient(180deg, rgba(var(--br-primary-rgb, 14,90,165), 0.10) 0%, rgba(109,92,246,0.06) 100%)",
                          border: "1px solid rgba(var(--br-primary-rgb, 14,90,165), 0.16)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: 900,
                              color: "rgba(15,23,42,0.85)",
                            }}
                          >
                            Account Manager
                          </div>
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 12,
                              color: "rgba(15,23,42,0.62)",
                            }}
                          >
                            View contact details for quick support.
                          </div>
                        </div>
                        <PrimaryButton
                          onClick={onOpenAccountManager}
                          style={{ padding: "10px 14px" }}
                        >
                          View →
                        </PrimaryButton>
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: 16,
                          borderRadius: "var(--br-card-radius, 18px)",
                          padding: 14,
                          background: "rgba(148,163,184,0.12)",
                          border: "1px solid rgba(148,163,184,0.22)",
                          color: "rgba(15,23,42,0.70)",
                          fontSize: 13,
                        }}
                      >
                        No account manager assigned yet. Please contact support
                        if you need help.
                      </div>
                    )}

                    <div
                      className="wizard-actions"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 18,
                        paddingBottom: 20,
                      }}
                    >
                      <PrimaryButton
                        disabled={isSubmitting}
                        onClick={handleWizardFinish}
                      >
                        {isSubmitting ? "Saving..." : "Go to dashboard →"}
                      </PrimaryButton>
                    </div>
                  </>
                ) : null}
              </div>

              {/* bottom “sparkle” footer */}
              <div
                className="wizard-footer"
                style={{
                  padding: "14px 22px 20px 22px",
                  borderTop: "1px solid rgba(148,163,184,0.22)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ fontSize: 12, color: "rgba(15,23,42,0.55)" }}>
                  🔒 Your data is used only to improve your experience.
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(var(--br-primary-rgb, 14,90,165), 0.85)",
                    fontWeight: 900,
                  }}
                >
                  {/* {BRAND} */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive tweaks */}
        <style>
          {`
          @media (max-width: 980px) {
            .wizard-grid { 
              grid-template-columns: 1fr !important; 
            }
          }

          @media (max-width: 560px) {
            .wizard-shell {
              padding: 18px 14px !important;
            }

            .wizard-hero,
            .wizard-main {
              border-radius: 18px !important;
            }

            .wizard-hero svg {
              max-height: 120px !important;
            }

            .wizard-title {
              font-size: 20px !important;
              line-height: 1.15 !important;
            }

            .wizard-subtitle {
              font-size: 12px !important;
            }

            .wizard-tiles {
              grid-template-columns: 1fr !important;
            }

            .wizard-pills {
              gap: 8px !important;
            }

            .wizard-footer {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 8px !important;
            }

            .wizard-actions {
              flex-direction: column !important;
              gap: 10px !important;
            }

            .wizard-actions button {
              width: 100% !important;
              justify-content: center !important;
            }
          }
        `}
        </style>
      </div>
    </Modal>
  );
};

export default WelcomeWizard;
